import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, TrendingDown, TrendingUp, Users } from "lucide-react";
import api from "../api";
import { useLanguage } from "../i18n";

export default function AdminConsents() {
  const { t, lang } = useLanguage();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/consents/report?month=${month}`).then(({ data }) => setData(data.data)).catch(() => setData({ error: true }));
  }, [month]);

  const card = "rounded-2xl border border-slate-200/80 p-4";
  const num = "font-display font-black text-2xl text-slate-900";
  const lbl = "font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400";

  return (
    <div data-testid="admin-consents-report" className="mt-10 rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#FF6B35]" /> {t("cst.title")}
        </h3>
        <input data-testid="cst-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-mono-t outline-none focus:border-[#FF6B35]" />
      </div>

      {!data ? (
        <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FF6B35]" /></div>
      ) : data.error ? (
        <p className="text-sm text-slate-400">{t("misc.error")}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <div className={card}><div className={num}>{data.totals.marketing_active}</div><div className={lbl}>{t("cst.mktActive")}</div></div>
            <div className={card}><div className={`${num} ${data.totals.marketing_revoked > 0 ? "text-red-500" : ""}`}>{data.totals.marketing_revoked}</div><div className={lbl}>{t("cst.mktRevoked")}</div></div>
            <div className={card}><div className={num}>{data.totals.marketing_unset}</div><div className={lbl}>{t("cst.mktUnset")}</div></div>
            <div className={card}><div className={num}>{data.totals.terms_accepted}</div><div className={lbl}>{t("cst.terms")}</div></div>
            <div className={card}><div className={num}>{data.totals.age_confirmed}</div><div className={lbl}>{t("cst.age")}</div></div>
            <div className={card}><div className={num}>{data.totals.users}</div><div className={lbl}>{t("cst.users")}</div></div>
          </div>

          <div className={lbl} style={{ marginBottom: 8 }}>{t("cst.monthMoves")} — {month}</div>
          <div className="flex flex-wrap gap-3 mb-6" data-testid="cst-month-moves">
            {Object.keys(data.by_kind).length === 0 && <span className="text-sm text-slate-400">{t("cst.noMoves")}</span>}
            {Object.entries(data.by_kind).map(([kind, v]) => (
              <span key={kind} data-testid={`cst-kind-${kind}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-mono-t">
                <span className="font-bold uppercase tracking-widest text-slate-500">{kind}</span>
                <span className="inline-flex items-center gap-1 text-emerald-600 font-bold"><TrendingUp className="w-3 h-3" />{v.granted}</span>
                <span className="inline-flex items-center gap-1 text-red-500 font-bold"><TrendingDown className="w-3 h-3" />{v.revoked}</span>
              </span>
            ))}
          </div>

          <div className={lbl} style={{ marginBottom: 8 }}>{t("cst.recent")}</div>
          <table className="w-full min-w-[680px] text-sm" data-testid="cst-recent-table">
            <tbody>
              {data.recent.length === 0 && (
                <tr><td className="py-6 text-sm text-slate-400">{t("cst.noMoves")}</td></tr>
              )}
              {data.recent.map((e) => (
                <tr key={e.log_id} data-testid={`cst-row-${e.log_id}`} className="border-b border-slate-50 last:border-0">
                  <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-500 whitespace-nowrap">
                    {new Date(e.ts).toLocaleString(lang, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-600">{e.email}</td>
                  <td className="py-2.5 pr-3">
                    <span className="rounded-full bg-slate-100 text-slate-500 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">{e.kind}</span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${e.granted ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                      {e.granted ? t("cst.granted") : t("cst.revoked")}
                    </span>
                  </td>
                  <td className="py-2.5 text-right font-mono-t text-[10px] text-slate-400">{e.source}{e.version ? ` · v${e.version}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
